/**
 * The emoji picker a reaction is chosen from, and the button that opens it.
 *
 * One module because the button has two homes and must be the SAME component in both: the
 * action bar offers it while the message has no reactions, and the reaction footer offers
 * it once it has. Exactly one of the two is ever mounted (see `channel-message-chrome`),
 * so there is never a moment with two add buttons on one bubble.
 *
 * A pick TOGGLES, exactly as clicking a pill does: picking an emoji the viewer has already
 * used takes it back. The picker and the row are two ways to reach the same control, so
 * one of them answering a repeat pick with silence would be the surprise.
 *
 * It goes through the caller's `toggleReaction` rather than comparing the pick against the
 * rendered reactions here — that comparison is the hook's, and only the hook can see the
 * viewer's live intents. A guard in this component would read a one-render-old snapshot and
 * could pick the wrong direction mid-burst.
 */

import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { cn } from "cn";
import { useTheme } from "next-themes";
import { type ReactNode, useState } from "react";

import { BW_EMOJI_PICKER_CATEGORIES } from "@/components/ui/editor/core/utils/bw-emoji";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toReactionType } from "../_libs/message-reaction";

/**
 * The emoji-mart picker in a popover, opened by `children`; hands back the reaction string.
 *
 * ## The popover is positioned against the slot, not against the button in it
 *
 * The trigger grows under the pointer (`buttonPillClassName`'s `hover:scale-110`), and
 * Radix positions against whatever it is anchored to. Anchored to the button, that
 * reference box is not fixed: measured, the 24px button reads 26.4px wide and 1.2px
 * further left while scaled, and `--radix-popover-trigger-width` is taken from the same
 * box. Every reposition while the pointer arrives or leaves reads a different rect.
 *
 * `PopoverAnchor` takes a separate element, so the trigger stays the button and the
 * POSITION comes from the plain box around it. `scale` is a paint-time transform: it
 * never touches the layout box, so this wrapper measures the button's resting size no
 * matter how far the button is scaled inside it. The animation is untouched and the
 * picker still opens off the button's own edge, because the wrapper IS the button's
 * resting edge.
 *
 * It must not carry a size: the slot shrink-wraps the trigger it is handed, so the same
 * wrapper holds a button of any size still. `shrink-0` because both its hosts are
 * `flex-nowrap` rows.
 *
 * ## The tooltip lives here
 *
 * The add-reaction button is the only control in the hover bar that is a picker rather than
 * a command, and it went without a label while every button beside it had one. Naming it is
 * this module's job because the nesting only works from the inside out — `TooltipTrigger` >
 * `PopoverTrigger` > the caller's button, since both triggers are `asChild` and need a real
 * element under them. A caller wrapping its button in a `Tooltip` itself would hand
 * `PopoverTrigger` a Radix root that renders no element.
 */
export function ReactionPickerPopover({
  align,
  tooltip,
  onPick,
  children,
}: {
  align: "start" | "end";
  /** What the trigger is called on hover — the same label in both of the button's homes. */
  tooltip: string;
  onPick: (type: string) => void;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, systemTheme } = useTheme();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverAnchor asChild>
        <div className={cn(/* sorted */ "flex", "shrink-0")}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>{children}</PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        </div>
      </PopoverAnchor>
      {/* Mounted only while open, rather than left to `PopoverContent`'s own exit
          animation. The pick closes the picker AND grows the reaction row beside its
          trigger in one commit; a content still fading out through that commit gets
          re-anchored by the growth and visibly slides — the jump this replaces. Taking
          the node out of the tree ends it in the same commit instead. (Suppressing the
          animation in CSS would not be deterministic: `animate-out` comes from
          `tailwindcss-animate`, which `cn`'s merge does not know, so an `animate-none`
          beside it is settled by stylesheet order rather than by the class list.) */}
      {isOpen && (
        <PopoverContent
          align={align}
          className={cn(
            /* sorted */
            "w-auto",
            "p-0",
            "bg-transparent border-none shadow-none",
            // emoji-mart sizes its own host: `width: min-content` (which the grid props
            // below settle) and a hard `height: 435px`, which is the composer picker's
            // height and far more than a one-shot reaction pick needs. Only the height
            // has to be fought in CSS, and it can be — the picker is a custom element, so
            // a rule from this document beats its shadow root's own `:host`.
            "[&_em-emoji-picker]:h-80",
          )}
        >
          {/* Sized for a reaction, not for composing. The composer's picker
              (`components/ui/editor/core/components/emoji-picker.tsx`) stays full size:
              it is a standing toolbar control inserting into a body of text, where this
              one opens off a hover cluster to take a single pick and close.

              8 per line at 32px (from 9 at 36) is ~296px wide against ~364px, which also
              keeps the popover from overhanging a narrow thread column; the glyphs stay
              at `emojiSize={20}`, so only the hit targets tighten. `maxFrequentRows={2}`
              halves the recent block, which at the default 4 rows opened the picker on 36
              emoji of history before any category. Search and the category nav both stay
              — they are what make the long tail reachable at this size. */}
          <Picker
            perLine={8}
            emojiButtonSize={32}
            emojiSize={20}
            maxFrequentRows={2}
            theme={theme === "system" ? systemTheme : theme}
            data={data}
            custom={BW_EMOJI_PICKER_CATEGORIES}
            previewPosition="none"
            onEmojiSelect={(emoji: { native?: string; src?: string }) => {
              setIsOpen(false);
              const type = toReactionType(emoji);
              if (type) onPick(type);
            }}
          />
        </PopoverContent>
      )}
    </Popover>
  );
}
